import { useCallback, useRef } from "react";
import { Scale } from "../constants/scales";
import { LoopStep } from "./loopSteps";
import convertMelodyRangeScaleToInternalState from "./useExternalState/convertMelodyRangeScaleToInternalState";
import { PlayerExternalState } from "./useExternalState/useExternalState";
import { TranspositionPattern } from "./usePlayer";
import { VocalRegister } from "../constants/vocalRegister";

export interface MutatableInternalState {
  steps: LoopStep[];
  stepIndex: number;

  scales: Scale[];
  scaleIndex: number;

  transpositionPattern: TranspositionPattern;
  transpositionDirection: "up" | "down";

  holdScale: boolean;
}

function initializeUnsyncedInternalState(
  externalState: PlayerExternalState
): MutatableInternalState {
  return {
    ...convertMelodyRangeScaleToInternalState(externalState),
    transpositionPattern: "up/down",
    transpositionDirection: "up",
    holdScale: false,
  };
}

export default function useMutatableInternalState(
  initialExternalState: PlayerExternalState
) {
  const internalState = useRef<MutatableInternalState | null>(null);

  const getInternalState = useCallback(() => {
    if (!internalState.current) {
      internalState.current =
        initializeUnsyncedInternalState(initialExternalState);

      return internalState.current;
    }

    return internalState.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mutateInternalState = useCallback(
    (newState: Partial<MutatableInternalState>) => {
      const state = getInternalState();

      Object.assign(state, newState);

      return state;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return { getInternalState, mutateInternalState };
}
